<template>
  <div>
    <HeaderBreadcrumbs :items="[
      { label: 'SmartPin', to: { name: 'smartpininsight' } },
      { label: 'Insight', to: { name: 'smartpininsight' } },
      { label: 'Locations' },
    ]" title="Locations" />

    <div class="flex w-full flex-col bg-white rounded-xl p-4 shadow-sm mb-3">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-xl mb-0 font-bold text-gray-900 tracking-tight">
          Summary Onsite Reader
        </h2>
        <div class="flex items-center gap-3">
          <DateRangePicker v-model="selectedDateRange" :max-days="31" @change="handleDateRangeChange" />
          <div class="w-56">
            <CustomSelected
              class="cursor-pointer"
              :value="selectedLocationOption"
              :options="locationOptions"
              label="name"
              track-by="value"
              :multiple="false"
              :default-text="'All Locations'"
              @input="handleLocationChange"
            />
          </div>
        </div>
      </div>

      <LocationSummaryCards :summary="summary" :is-loading="isLoadingSummary" />
    </div>

    <div class="flex w-full flex-col bg-white rounded-xl p-4 shadow-sm mb-5">
      <div class="flex justify-between items-center mb-4">
        <div class="relative w-96">
          <SearchOutline className="absolute top-1/2 left-3 transform -translate-y-1/2" />
          <input
            v-model="searchQuery"
            type="search"
            placeholder="Search by Tag Location, Name or Email"
            class="w-full py-2.5 pl-9 pr-3 text-sm border border-gray-300 rounded-lg bg-gray-50"
            @input="onSearch"
          />
        </div>
        <ExportCsvButton
          :is-empty="!totalRows"
          :is-exporting="isExporting"
          :countdown="exportCountdown"
          :countdown-text="formattedExportCountdown"
          @click="exportCsv"
        />
      </div>

      <DataTable
        :headers="headers"
        :data="visitors"
        :row-key="['location_name', 'name', 'content_title', 'purchase', 'subscribe']"
        :currentPage="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        :withNumberCol="false"
        :withAction="false"
        :withScrollX="true"
        className="w-full"
      >
        <template #header-purchase>
          <div class="text-right">Purchase (IDR)</div>
        </template>
        <template #header-subscribe>
          <div class="text-right">Subscribe (IDR)</div>
        </template>
        <template #location_name="{ row }">
          {{row.tag_location || '-'}}
        </template>
        <template #name="{ row }">
          <div>{{ row.name || '-' }}</div>
          <div v-if="row.email" class="text-xs italic normal-case text-gray-500">{{ row.email }}</div>
        </template>
        <template #content_title="{ value }">
          <span class="normal-case" :title="value || '-'">{{ value || '-' }}</span>
        </template>
        <template #purchase="{ value }">
          <div class="text-right">{{ value || '-' }}</div>
        </template>
        <template #subscribe="{ value }">
          <div class="text-right">{{ value || '-' }}</div>
        </template>
        <template #empty>
          <InsightEmptyState :variant="appliedSearchQuery ? 'search' : 'empty'" />
        </template>
      </DataTable>

      <div class="flex justify-between pt-4 pl-3 pr-3 mt-1 bg-white border-t border-gray-100" v-if="totalRows">
        <div class="font-semibold">
          <span class="font-normal text-gray-500">Showing </span>
          {{ firstRowNumber }}-{{ lastRowNumber }}
          <span class="font-normal text-gray-500">of</span> {{ totalRows }}
        </div>
        <div class="mb-2">
          <t-pagination :disabled="isLoading" :total-items="totalRows" :per-page="perPage" v-model="currentPage"
            @change="onChangePage" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs.vue';
import DateRangePicker from '@/components/form/DateRangePicker.vue';
// Nama tag bukan `Table` supaya tidak dibaca Sonar sebagai elemen <table> HTML (rule: table butuh <th>)
import DataTable from '@/components/form/table.vue';
import CustomSelected from '@/components/form/CustomSelected.vue';
import LocationSummaryCards from '@/components/smart-pin/LocationSummaryCards.vue';
import InsightEmptyState from '@/components/smart-pin/InsightEmptyState.vue';
import ExportCsvButton from '@/components/smart-pin/ExportCsvButton.vue';
import SearchOutline from '@/components/icons/SearchOutline';
import insightFilterMixin, { toQueryDate } from '@/components/smart-pin/mixins/insightFilterMixin';
import exportCooldownMixin from '@/components/smart-pin/mixins/exportCooldownMixin';

export default {
  name: 'SmartPinInsightLocation',
  components: {
    HeaderBreadcrumbs,
    DateRangePicker,
    DataTable,
    CustomSelected,
    LocationSummaryCards,
    InsightEmptyState,
    ExportCsvButton,
    SearchOutline,
  },
  mixins: [
    insightFilterMixin,
    exportCooldownMixin('smartpin_insight_location_export_until'),
  ],
  data() {
    return {
      summary: {},
      isLoadingSummary: false,
      visitors: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      isLoading: false,
      searchQuery: '',
      appliedSearchQuery: '',
      searchTimeout: null,
      headers: [
        { label: '', key: 'no' },
        { label: 'Tag Locations', key: 'location_name' },
        { label: 'Name', key: 'name' },
        { label: 'Content Title', key: 'content_title' },
        { label: 'Purchase (IDR)', key: 'purchase', slotHeader: 'header-purchase' },
        { label: 'Subscribe (IDR)', key: 'subscribe', slotHeader: 'header-subscribe' },
      ],
    };
  },
  computed: {
    firstRowNumber() {
      return (this.currentPage - 1) * this.perPage + 1;
    },
    lastRowNumber() {
      return Math.min(this.currentPage * this.perPage, this.totalRows);
    },
  },
  mounted() {
    this.fetchFilteredData();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout);
  },
  methods: {
    ...mapActions('smartpin', [
      'getInsightLocationSummary',
      'getInsightLocationVisitors',
      'downloadInsightLocationVisitorsCsv',
    ]),

    onFilterChange() {
      this.currentPage = 1;
      this.fetchFilteredData();
    },

    onSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchVisitors();
      }, 500);
    },

    exportCsv() {
      if (!this.totalRows) return;

      const [startDate, endDate] = this.selectedDateRange;
      this.runExport(() => this.downloadInsightLocationVisitorsCsv({
        ...this.filterPayload,
        query: this.searchQuery.trim(),
        fileName: `insight-location-visitors_${toQueryDate(startDate)}_${toQueryDate(endDate)}.csv`,
      }));
    },

    fetchFilteredData() {
      this.fetchSummary();
      this.fetchVisitors();
    },

    async fetchSummary() {
      this.isLoadingSummary = true;

      try {
        this.summary = await this.getInsightLocationSummary(this.filterPayload);
      } catch (error) {
        this.summary = {};
        this.alertTopEnd('error', error?.response?.data?.message || 'Failed to load location summary', false);
      } finally {
        this.isLoadingSummary = false;
      }
    },

    async fetchVisitors() {
      this.isLoading = true;

      const query = this.searchQuery.trim();

      try {
        const { rows, count } = await this.getInsightLocationVisitors({
          ...this.filterPayload,
          page: this.currentPage,
          limit: this.perPage,
          query,
        });

        this.visitors = rows;
        this.appliedSearchQuery = query;
        console.log('Fetched visitors:', rows);
        this.totalRows = count;
      } catch (error) {
        this.visitors = [];
        this.totalRows = 0;
        this.alertTopEnd('error', error?.response?.data?.message || 'Failed to load visitors', false);
      } finally {
        this.isLoading = false;
      }
    },

    onChangePage() {
      this.fetchVisitors();
    },
  },
};
</script>
